// react
import React, {useEffect, useMemo, useState} from 'react'
// antd
import {Button, Pagination, Spin, Switch, Modal, message, Divider, Tag, Space} from 'antd'
// component
import {EmptyPanel, ListItemBlockComponent} from '@/components/elements'
import CompanyFlowDrawer from '@/pages/company/flow'
// util
import {randomCoding} from '@/utils'
// icon
import {ExclamationCircleOutlined} from '@ant-design/icons'
// antd-pro
import ProCard from '@ant-design/pro-card'
// api
import Api from '@/api'
import FlowLogs from '@/pages/company/logs'


interface IQueryEnType {
  current: number
  size: number
  keyWord?: string
  corpId: string
}

interface FlowItem {
  id: string
  flowName: string
  flowDesc: string
  enabled: number
  state: number
}

interface IProps {
  corpId: string
}

/**
 * 流程card
 * @param props
 * @constructor
 */
const FlowListCard = (props: IProps) => {

  const [ShowFlow, setShowFlow] = useState<boolean>(false)
  const [QueryEn, setQueryEn] = useState<IQueryEnType>({
    current: 1,
    size: 10,
    corpId: props.corpId
  })
  const [FlowList, setFlowList] = useState<Array<FlowItem>>([])
  const [Total, setTotal] = useState<number>(0)
  const [EditEn, setEditEn] = useState<FlowItem>()
  const [FlowLoading, setFlowLoading] = useState<boolean>(false)
  const [FlowId, setFlowId] = useState<string>("")

  /**
   * 获取流程列表
   */
  const getFlowList = async () => {
    setFlowLoading(true)
    const _data: any = await Api.Company.FlowList(QueryEn).finally(() => setFlowLoading(false))
    if (_data) {
      setFlowList(_data.records)
      setTotal(_data.total)
    }
  }

  useEffect(() => {
    if (props.corpId) {
      getFlowList()
    }
  }, [props.corpId, QueryEn])

  /**
   * 状态的开关
   * @param e
   * @param item
   */
  const stateChange = (e: boolean, item: FlowItem) => {
    Modal.confirm({
      title: '确认',
      icon: <ExclamationCircleOutlined/>,
      content: `是否确认${e ? '开启' : '关闭'}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        setFlowLoading(true)
        const _data = await Api.Company.FlowEnabled({
          id: item.id,
          enabled: e ? 1 : 0
        }).finally(() => setFlowLoading(false))
        if (_data) {
          message.success("操作成功！")
          getFlowList()
        }
      }
    })
  }

  /**
   * 状态的开关
   * @param id
   */
  const start = (id: string) => {
    Modal.confirm({
      title: '确认',
      icon: <ExclamationCircleOutlined/>,
      content: `是否确认执行？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        setFlowLoading(true)
        const _data = await Api.Company.FlowStart({id}).finally(() => setFlowLoading(false))
        if (_data) {
          message.success("操作成功！")
          getFlowList()
        }
      }
    })
  }

  /**
   * 删除流程
   * @param id
   */
  const deleteFlow = (id: string) => {
    Modal.confirm({
      title: '确认',
      icon: <ExclamationCircleOutlined/>,
      content: `是否确认删除流程？`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        const _data = await Api.Company.FlowDelete({id})
        if (_data) {
          message.success("操作成功！")
          getFlowList()
        }
      }
    })
  }

  const getTagName = (state: number) => {
    switch (state) {
      case 0:
      default:
        return '初始化'
      case 1:
        return '执行中'
      case 2:
        return '停止'
      case 3:
        return '执行完成'
    }
  }


  const listDom = useMemo(() => {
    if (FlowList.length == 0) {
      return <EmptyPanel description={"暂无流程数据"}/>
    }

    // <Tag key={randomCoding()}>{getTagName(item.state)}</Tag>,

    return <>
      {
        FlowList.map((item: FlowItem, index: number) => {
          return <ListItemBlockComponent
            key={index}
            title={item.id+" "+item.flowName}
            subTitle=""
            explain={item.flowDesc}
            coverText={getTagName(item.state)}
            extra={[
              <Space key={randomCoding()} split={<Divider className={"bg-gray-300"} type={"vertical"}/>}>
                <a
                  onClick={() => {
                    setShowFlow(!ShowFlow)
                    setEditEn(item)
                  }}>编辑</a>
                <a onClick={() => setFlowId(item.id)}>日志</a>
                <a className={"hover:text-[red]"} onClick={() => deleteFlow(item.id)}>删除</a>
                <Switch
                  onChange={(e) => stateChange(e, item)}
                  checked={item.enabled == 1}
                />
                <Button
                  type={"primary"}
                  onClick={() => start(item.id)}
                >
                  执行
                </Button>
              </Space>
            ]}
          />
        })
      }
    </>
  }, [FlowList])

  return (
    <ProCard
      className={"mt-[24px]"}
      title={"流程信息"}
      bordered
      extra={
        <Space>
          <Button onClick={getFlowList}>刷新流程</Button>
          <Button type={"primary"} onClick={() => {
            setShowFlow(!ShowFlow)
            setEditEn(undefined)
          }}>新建流程</Button>
        </Space>
      }
    >
      {ShowFlow && <CompanyFlowDrawer
          corpId={props.corpId}
          en={EditEn}
          close={(reload: boolean) => {
            setShowFlow(!ShowFlow)
            reload && getFlowList()
          }}/>
      }
      {FlowId && (
        <FlowLogs flowId={FlowId} close={() => setFlowId("")}/>
      )}
      <Spin spinning={FlowLoading}>
        {listDom}
      </Spin>
      {
        Total > QueryEn.size && <Pagination
              className={"mt-[16px] flex justify-end"}
              current={QueryEn.current}
              pageSize={QueryEn.size}
              size={"small"}
              total={Total}
              showTotal={(total, range) => `${range[0]}-${range[1]}, 共 ${total} 条`}
              onChange={(page, pageSize) => {
                setQueryEn({
                  ...QueryEn,
                  current: page,
                  size: pageSize
                })
              }}
          />
      }

    </ProCard>
  )
}

export default FlowListCard
